import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import Grid from 'UIKit/Box/Grid'
import { BoxProps } from 'UIKit/Box/types'
import Image from 'UIKit/Image'
import Link from 'UIKit/Link'
import Page from 'UIKit/Page'
import Text from 'UIKit/Text'
import { RouteConfig } from 'config/constants/route'
import { ActionQueryEnum } from 'hooks/useActionQueryListener'
import useMatchBreakpoints from 'hooks/useMatchBreakpoints'
import React, { useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useAllTokens } from 'state/app/hooks'
import styled from 'styled-components'
import { Icons } from 'svgs'
import hunnyTheme from 'theme'
import DisplaySocialMedia from 'views/BonusCenter/components/DisplaySocialMedia/DisplaySocialMedia'
import Column from './Column'
import HunnyPartner from './Menu/HunnyPartner'
import MenuFooterExpand from './Menu/MenuFooterExpand'
import { RowBetween, RowCenter } from './Row'

const listLink = [
  {
    menu: {
      content: 'PRODUCTS',
    },
    subMenus: [
      {
        content: 'HunnyPoker',
        to: RouteConfig.HunnyPoker,
        isExternal: true,
        id: 'hunny-poker',
        attributes: {
          rel: 'nofollow',
        },
      },
      {
        content: 'HunnySwap',
        to: RouteConfig.HunnySwap,
        isExternal: true,
        id: 'hunny-swap',
        attributes: {
          rel: 'nofollow',
        },
      },
      {
        content: 'Farm',
        to: RouteConfig.HunnyFarm,
        isExternal: true,
        id: 'farm',
        attributes: {
          rel: 'nofollow',
        },
      },
    ],
  },
  {
    menu: {
      content: 'FEATURES',
    },
    subMenus: [
      {
        content: 'Staking',
        to: RouteConfig.Stake,
        isExternal: false,
        id: 'desktop-staking',
      },
      {
        content: 'Cashback',
        to: RouteConfig.Cashback,
        isExternal: false,
        id: 'cashback',
      },
      {
        content: 'Bonus (HUSD)',
        to: `?action=${ActionQueryEnum.UnlockHUSD}`,
        id: 'bonus-husd',
      },
      {
        content: 'VIP Benefit',
        to: `?action=${ActionQueryEnum.OpenAllLevel}`,
        id: 'vip-benefit',
      },
      {
        content: 'Earn Referral',
        to: RouteConfig.Referral,
        id: 'earn-referral',
      },
    ],
  },
  {
    menu: {
      content: 'PROMO',
    },
    subMenus: [
      {
        content: 'Hot Promotions',
        to: RouteConfig.Promotions,
        isExternal: false,
        id: 'hot-promotion',
      },
      {
        content: 'Promo Materials',
        to: RouteConfig.PromotionMaterials,
        isExternal: true,
        id: 'promo-material',
        attributes: {
          rel: 'nofollow',
        },
      },
    ],
  },
  {
    menu: {
      content: 'HELP',
    },
    subMenus: [
      {
        content: 'Term of Service',
        to: RouteConfig.TermOfService,
        isExternal: true,
        id: 'term-of-service',
      },
      {
        content: 'Blog',
        to: RouteConfig.HunnyBlogFinance,
        isExternal: true,
        id: 'blog',
      },
      {
        content: 'Documents',
        to: RouteConfig.HunnyDocs,
        isExternal: true,
        id: 'documents',
        attributes: {
          rel: 'nofollow',
        },
      },
    ],
  },
]

const Footer: React.FC<BoxProps> = ({ ...props }) => {
  const { isTablet, isDesktop } = useMatchBreakpoints()
  const { t } = useTranslation()
  const tokens = useAllTokens(
    (token, index, tokenList) =>
      token && tokenList.findIndex((item) => item?.logo === token?.logo) === index && token.code !== 'HUSD',
  )

  const renderLinkDesktop = useMemo(
    () =>
      listLink.map((link, index) => (
        <StyleWrapLink key={`footer-nav-col-link${index}`}>
          <Text width="max-content" fontSize="12px" color="text" fontWeight="bold" letterSpacing="0.1em" mb="6px">
            <Trans>{link.menu.content}</Trans>
          </Text>
          {link.subMenus.map((subMenu) => {
            return (
              <Link
                external={subMenu.isExternal}
                title={subMenu.content}
                id={subMenu.id}
                width="max-content !important"
                fontSize="12px"
                my="6px"
                color="textTertiary"
                key={`footer-link-nav-col-link${subMenu.id}`}
                href={subMenu.to || ''}
                {...(subMenu?.attributes || {})}
              >
                <Trans>{subMenu.content}</Trans>
              </Link>
            )
          })}
        </StyleWrapLink>
      )),
    [listLink, t],
  )

  const renderLinkMobile = useMemo(
    () =>
      listLink.map((link, menuIndex) => (
        <MenuFooterExpand
          key={`menu-link-footer-mobile-${menuIndex}`}
          menu={link.menu}
          subMenus={link.subMenus}
          menuHeight={30}
        />
      )),
    [listLink, t],
  )

  return (
    <StyledFooter id="footer" {...props}>
      <StyledDisclaimerContainer>
        <StyleWrapLogoAndDescription>
          <RowBetween width="100%">
            <Image src="/logo-text.png" height={46} width={155} alt="logo" />
          </RowBetween>

          <Text
            mt={['12px', '20px']}
            fontWeight={400}
            fontSize="12px"
            lineHeight="16px"
            letterSpacing="-0.02em"
            color="textTertiary"
          >
            <Trans>
              This website offers gaming with risk experience. To be a user of our site you must be over 18 years old.
              We are not responsible for the violation of your local laws related to i-gaming. Play responsibly and have
              fun on HunnyPlay.
            </Trans>
          </Text>
          <Flex flexDirection="column">
            <Flex alignItems="center" m={['12px 0 22px', '16px 0 22px']}>
              <Box width="16px" mr="4px">
                <Icons.CopyRightIcon fill={hunnyTheme.colors.textSubtle} />
              </Box>
              <Text fontSize="10px" color="textSubtle">
                <Trans>2021-{{ year: new Date().getFullYear() }} HunnyPlay All Rights Reserved</Trans>
              </Text>
            </Flex>

            <DisplaySocialMedia />
          </Flex>
        </StyleWrapLogoAndDescription>
        {isTablet || isDesktop ? (
          <WrapColLink>{renderLinkDesktop}</WrapColLink>
        ) : (
          <Box width={'100%'}>{renderLinkMobile}</Box>
        )}
      </StyledDisclaimerContainer>
      <RowBetween>
        <Column maxWidth={['', , , , , '70%']}>
          <Text
            mt={['12px', '20px']}
            mb="8px"
            fontWeight={400}
            fontSize="12px"
            lineHeight="20px"
            letterSpacing="-0.02em"
            color="textSubtle"
          >
            <Trans>Payment methods</Trans>
          </Text>
          <StyledSupportListToken>
            {tokens?.map((token) => (
              <Image key={token.code} src={token.logo} width={25} height={25} style={{ margin: '5px' }} />
            ))}
          </StyledSupportListToken>
        </Column>
      </RowBetween>
      <Box mt="20px">
        <HunnyPartner />
      </Box>
      <RowCenter>
        <Text
          fontSize="12px"
          fontWeight={400}
          lineHeight="16px"
          textAlign="center"
          color="textSubtle"
          maxWidth="1200px"
          mt="10px"
        >
          <Trans>
            HunnyPlay is operated by Alchemy Games N.V., a company registered and established under the laws of Curacao
            Alchemy Games N.V.is licensed and regulated by Antillephone N.V. (license no. 8048/JAZ2024-001). Alchemy
            Games N.V., registration number is 164974 and its registered address is Hanchi Snoa 19 Trias Building
            Willemstad, Curaçao.
          </Trans>
        </Text>
      </RowCenter>
    </StyledFooter>
  )
}

const StyledFooter = styled(Page)`
  padding-top: 20px;

  width: 100%;
  flex-direction: column;
  justify-content: space-between;
  box-sizing: border-box;

  ${({ theme }) => theme.mediaQueries.md} {
    padding-top: 32px;
  }
`

const StyledSupportListToken = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
`

const StyledContainer = styled(Flex)`
  width: 100%;
  box-sizing: border-box;
  justify-content: space-between;
`

const StyleWrapLogoAndDescription = styled(Flex)`
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  margin-bottom: 16px;

  ${({ theme }) => theme.mediaQueries.sm} {
    margin-bottom: 32px;
    width: 100%;
    padding-right: 0;
  }

  ${({ theme }) => theme.mediaQueries.xl} {
    width: 45%;
    padding-right: 5%;
    margin-bottom: 0;
  }
`

const StyledDisclaimerContainer = styled(StyledContainer)`
  height: auto;
  width: 100%;
  flex-wrap: wrap;
  flex-direction: column;

  ${({ theme }) => theme.mediaQueries.xl} {
    flex-direction: row;
    align-items: flex-start;
  }
`

const WrapColLink = styled(Grid)`
  width: 85%;
  gap: 0 40px;
  grid-template-columns: repeat(4, 1fr);
  align-items: flex-start;

  ${({ theme }) => theme.mediaQueries.sm} {
    width: 100%;
  }

  ${({ theme }) => theme.mediaQueries.xl} {
    width: 50%;
    gap: 0 20px;
  }
`

const StyleWrapLink = styled(Flex)`
  width: calc(33% - 80px);
  flex-direction: column;
  justify-content: flex-end;
`

export default Footer
